import { BaseTool, IToolContext, IToolManifest } from '../../../core/tools/interfaces/ITool';
import type { ICommand } from '../../../core/commands/ICommand';
import { CompositeCommand } from '../../../core/commands/ICommand';
import { CreateNodeCommand } from '../../../core/commands/CreateNodeCommand';
import { CreateEdgeCommand } from '../../../core/commands/CreateEdgeCommand';
import { useDiagramStore } from '../../../stores/diagramStore';
import type { Node } from '../../../core/models/scene';
import i18n from '../../../i18n/config';
import { bindGenogramToolName } from './utils/toolNameI18n';
import { SessionMetrics } from '../../analytics/services/SessionMetrics';

const PERSON_SIZE = 60;
const SPACING = 120;

export class AddPartnerTool extends BaseTool {
  manifest: IToolManifest = {
    id: 'genogram-add-partner',
    name: i18n.t('genogram:tools.addPartner', { defaultValue: 'Add Partner' }) as string,
    icon: '💑',
    category: 'create',
    description: 'Add a partner to the selected person',
    version: '1.0.0',
    supportedDiagramTypes: ['genogram' as any],
    requiresSelection: true,
    minSelection: 1,
    maxSelection: 1,
    ui: {
      toolbar: false,
      contextMenu: false,
      propertyPanel: false,
      sidebar: false,
      floatingPanel: true,
      statusBar: false
    },
    applicability: {
      contextTypes: ['person'],
      diagramTypes: ['genogram'],
    },
    capabilities: {
      modifiesShapes: false,
      createsShapes: true,
      deletesShapes: false,
      modifiesConnectors: false,
      createsConnectors: true,
      deletesConnectors: false,
      requiresUserInput: false,
      supportsMultiSelect: false,
      supportsBatchOperations: false
    },
    shortcuts: [
      { key: '1', description: 'Add partner (when one person selected)' }
    ]
  };

  constructor() {
    super();
    bindGenogramToolName(this.manifest, 'tools.addPartner', 'Add Partner');
  }

  canExecute(context: IToolContext): boolean {
    if (!super.canExecute(context)) return false;

    // Check that selection is a single person
    const summary = context.selectionSummary;
    return summary?.contextType === 'person';
  }

  execute(): ICommand | null {
    if (!this.context) return null;

    const selectedId = Array.from(this.context.selection)[0];
    const state = useDiagramStore.getState();
    const selectedNode = state.nodes.find(n => n.id === selectedId);

    if (!selectedNode) return null;

    const pos = { x: selectedNode.x, y: selectedNode.y };
    const size = {
      width: (selectedNode as any).w ?? (selectedNode as any).width ?? PERSON_SIZE,
      height: (selectedNode as any).h ?? (selectedNode as any).height ?? PERSON_SIZE,
    } as { width: number; height: number };

    // Position partner to the right
    const newNodeId = `person-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newNode: Node = {
      id: newNodeId,
      type: 'person',
      name: i18n.t('genogram:relationships.partner', { defaultValue: 'Partner' }) as string,
      sex: 'unknown',
      x: pos.x + size.width + SPACING,
      y: pos.y,
      w: size.width,
      h: size.height,
      width: size.width,
      height: size.height,
      category: 'genogram',
    } as Node;

    const nodeCommand = new CreateNodeCommand(newNode);

    const edgeCommand = new CreateEdgeCommand({
      type: 'marriage',
      from: selectedId,
      to: newNodeId,
      category: 'genogram',
      fromAnchor: 'right',
      toAnchor: 'left',
    });

    // Track quick-add usage
    SessionMetrics.increment('quick_adds');
    SessionMetrics.firstTime('quick_add');
    
    return new CompositeCommand('Add Partner', [nodeCommand, edgeCommand]);
  }
}
